import React, { useState, useEffect, useRef } from "react";
import PageTitle from "../../layouts/PageTitle";
import Swal from "sweetalert2";
import { scrapeWebsiteData, getLastScrapeInfo } from "../../../services/ScraperService";

const Scraper = () => {
  const [scraping, setScraping] = useState(false);
  const [lastScrape, setLastScrape] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const hasFetched = useRef(false);

  const fetchLastScrapeInfo = async () => {
    try {
      setLoadingInfo(true);
      const response = await getLastScrapeInfo();
      setLastScrape(response.data?.data || response.data || null);
    } catch (error) {
      console.error("Error fetching last scrape info:", error);
      setLastScrape(null);
    } finally {
      setLoadingInfo(false);
    }
  };

  useEffect(() => {
    if (!hasFetched.current) {
      fetchLastScrapeInfo();
      hasFetched.current = true;
    }
  }, []);

  const handleScrape = async () => {
    const confirm = await Swal.fire({
      title: "Start Scraping?",
      text: "This will scrape the AI App website data. Do you want to continue?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Scrape!",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      setScraping(true);
      await scrapeWebsiteData();
      Swal.fire("Success!", "Website scraping completed successfully.", "success");
      fetchLastScrapeInfo();
    } catch (err) {
      console.error("Scrape error:", err);
      Swal.fire(
        "Error!",
        err?.response?.data?.message || "Failed to scrape website data.",
        "error"
      );
    } finally {
      setScraping(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  return (
    <>
      <PageTitle
        activeMenu="AI App Scraper"
        motherMenu="Scraper"
        activeMenuLink="scraper"
        motherMenuLink="scraper"
      />

      <div className="row">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">AI App Data Scraper</h4>
            </div>
            <div className="card-body">
              <p className="text-muted mb-4">
                Press the button below to scrape the latest data from the AI App website.
              </p>

              <div className="mb-4">
                <button
                  className="btn btn-primary btn-lg"
                  onClick={handleScrape}
                  disabled={scraping}
                >
                  {scraping ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Scraping...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-spider me-2"></i>
                      Scrape Now
                    </>
                  )}
                </button>
              </div>

              <hr />

              <div className="mt-4">
                <h5 className="mb-3">Last Scrape Info</h5>
                {loadingInfo ? (
                  <p className="text-muted">Loading...</p>
                ) : lastScrape ? (
                  <div className="table-responsive">
                    <table className="table table-bordered w-auto">
                      <tbody>
                        <tr>
                          <th className="text-black" style={{ width: "200px" }}>Last Scraped At</th>
                          <td>{formatDateTime(lastScrape.scraped_at || lastScrape.createdAt || lastScrape.updated_at)}</td>
                        </tr>
                        {lastScrape.status && (
                          <tr>
                            <th className="text-black">Status</th>
                            <td>
                              <span
                                className={
                                  lastScrape.status === "success"
                                    ? "badge badge-success"
                                    : "badge badge-danger"
                                }
                              >
                                {lastScrape.status}
                              </span>
                            </td>
                          </tr>
                        )}
                        {lastScrape.message && (
                          <tr>
                            <th className="text-black">Message</th>
                            <td>{lastScrape.message}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted">No scrape history found.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Scraper;
